export type Lang = 'en' | 'es'

export const CONTACT = {
  phone: '415-745-6692',
  phoneHref: 'tel:+14157456692',
  email: 'acruzdemo@gmail.com',
  whatsappHref: 'https://wa.me/14157456692',
  location: 'San Francisco, CA 94112',
}

export const LOGO = {
  src: 'https://i.postimg.cc/QtFff8F4/Logo-Armando-Junk.jpg',
  alt: 'Armando Junk Removal and Hauling logo',
}

export const serviceIcons = [
  'Trash2',
  'Hammer',
  'HardHat',
  'Warehouse',
  'PaintRoller',
] as const

export const benefitIcons = [
  'Zap',
  'BadgeDollarSign',
  'Users',
  'PackageCheck',
] as const

export const stepIcons = ['Camera', 'MessageSquareQuote', 'Truck'] as const

export const galleryShots = [
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/4fa0ad01-7ac7-411a-9261-8cc5c682dc27.jpg',
    alt: 'Crew loading an old sofa and broken furniture into a dump truck on a residential street',
    featured: true,
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/2029dc96-d54e-44e9-ba79-4546c62252d6.jpg',
    alt: 'Emptied and swept residential garage with the roll-up door open',
    featured: false,
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/f63ba91f-3edc-4780-b270-1af468ca1033.jpg',
    alt: 'Construction site with drywall and lumber debris stacked for removal',
    featured: false,
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/6d81d5f3-dd00-4937-bc08-0934f8ded525.jpg',
    alt: 'Cleaner in gloves wiping down and sanitizing a kitchen counter during a deep clean',
    featured: false,
  },
]

const en = {
  nav: {
    ariaMain: 'Main navigation',
    ariaMobile: 'Mobile navigation',
    logoSub: 'Junk Removal',
    links: [
      { label: 'Services', href: '#services' },
      { label: 'About Us', href: '#why-us' },
      { label: 'Gallery', href: '#gallery' },
      { label: 'Contact', href: '#contact' },
    ],
    cta: 'Get a Free Quote',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    languageLabel: 'Select language',
  },
  hero: {
    tag: 'SAN FRANCISCO, CA 94112',
    headline: "San Francisco's Trusted Junk Removal & Hauling Experts",
    subheadPre: 'Fast, affordable and reliable. Same-day or next-day service available. ',
    subheadStrong: 'Se habla español.',
    imageAlt:
      'Armando Junk Removal dump truck loaded with debris parked near the Golden Gate Bridge at sunrise',
    callCta: 'Call Now:',
    quoteCta: 'Get a Free Quote',
  },
  trust: {
    aria: 'Why you can trust us',
    items: [
      'Licensed & Insured',
      'Same-Day Service',
      'Free Estimates',
      'Se Habla Español',
    ],
  },
  services: {
    title: 'What We Do',
    intro:
      'One crew for the whole job — hauling, clearing, tearing out and finishing. Residential and commercial, all across San Francisco.',
    cardCta: 'Get a quote',
    items: [
      {
        title: 'Junk Removal',
        description:
          'Furniture, appliances, old electronics and more. Fast and clean.',
      },
      {
        title: 'Demolition Services',
        description: 'Assessment, site prep, demolish and debris disposal.',
      },
      {
        title: 'Construction Debris',
        description: 'We clear your job site so you can keep working.',
      },
      {
        title: 'Garage Cleaning',
        description: 'Full garage cleanouts, organized and efficient.',
      },
      {
        title: 'Deep Cleaning',
        description:
          'Professional deep cleaning services for residential and commercial spaces. Complete sanitization and detailed cleaning.',
      },
    ],
    promptTitle: 'Not sure what you need?',
    promptBody:
      "Send a photo of the job and we'll tell you exactly what it takes — and what it costs.",
    promptCta: 'Ask Armando',
  },
  why: {
    title: 'Why San Francisco Chooses Armando',
    intro:
      'A local, owner-operated crew that answers the phone, quotes honestly and finishes the job the same day whenever we can.',
    basedInLabel: 'Based in',
    basedInValue: 'SF 94112',
    servingLabel: 'Serving',
    servingValue: 'The Bay Area',
    items: [
      {
        title: 'Fast Response',
        description: 'Same-day or next-day availability across the city.',
      },
      {
        title: 'Affordable Pricing',
        description: 'No hidden fees. Upfront quotes before we lift a thing.',
      },
      {
        title: 'Experienced Team',
        description: 'Years of hands-on experience throughout the Bay Area.',
      },
      {
        title: 'Full Service',
        description: 'From debris to demolition, we handle it all.',
      },
    ],
  },
  steps: {
    title: '3 Simple Steps',
    intro: 'From photo to hauled away — most jobs are quoted in minutes.',
    whatsappCta: 'Send a photo on WhatsApp',
    items: [
      {
        number: '01',
        title: 'Send us a photo',
        description:
          'Snap a picture of your junk and send it over WhatsApp — no site visit needed.',
      },
      {
        number: '02',
        title: 'Get a free quote',
        description:
          'We reply within minutes with a clear, upfront price. No hidden fees.',
      },
      {
        number: '03',
        title: 'We show up and haul',
        description:
          'Our crew arrives on schedule, clears everything out and sweeps up after.',
      },
    ],
  },
  gallery: {
    title: 'Recent Work',
    intro:
      'A few jobs from around the city — hauled, cleared and cleaned up the same day.',
    captions: [
      'Furniture haul — Excelsior',
      'Full garage cleanout',
      'Job site debris clearing',
      'Deep cleaning',
    ],
  },
  testimonials: {
    title: 'What Our Clients Say',
    intro: 'Neighbors, landlords and contractors across the city.',
    ratingAria: 'Rated 5 out of 5 stars',
    items: [
      {
        name: 'Maria R.',
        location: 'Excelsior District',
        quote:
          'I sent a photo of my garage on Saturday morning and by the afternoon it was completely empty. Armando and his crew were quick, polite and left the floor spotless.',
      },
      {
        name: 'James T.',
        location: 'Bernal Heights',
        quote:
          'Got three quotes for a construction debris haul. Armando was the most affordable and the only one who showed up the next day. Straightforward pricing, no surprises.',
      },
      {
        name: 'Carlos M.',
        location: 'Mission District',
        quote:
          'Profesionales de principio a fin. They handled a full demolition and cleanout, worked hard all day and treated my property with respect. Highly recommended.',
      },
    ],
  },
  faq: {
    title: 'Frequently Asked Questions',
    intro:
      'Everything you need to know before booking your junk removal in San Francisco.',
    items: [
      {
        question: 'How much does junk removal cost in San Francisco?',
        answer:
          "Every job is different, which is why we offer free estimates before we start. Pricing depends on the volume and type of items. Send us a photo on WhatsApp and we'll give you an upfront price — usually within minutes. No hidden fees.",
      },
      {
        question: 'Do you offer same-day service?',
        answer:
          'Yes! We offer same-day and next-day service depending on availability. Call us at 415-745-6692 or message us on WhatsApp to check our schedule for today.',
      },
      {
        question: 'What types of junk do you remove?',
        answer:
          "We remove almost everything: furniture, appliances, mattresses, electronics, yard debris, construction waste, garage cleanouts and more. If you're not sure, just send us a photo and we'll let you know.",
      },
      {
        question: "Is there anything you won't take?",
        answer:
          "For safety and legal reasons, we cannot haul hazardous materials such as chemicals, paint, asbestos or medical waste. For everything else, we've got you covered.",
      },
      {
        question: 'Do I need to be home during the pickup?',
        answer:
          "Not necessarily. As long as we have access to the items and a way to communicate with you for payment, you don't need to be present. We'll send you a confirmation when the job is done.",
      },
      {
        question: 'How do I get a quote?',
        answer:
          'The easiest way is to send us a photo of the items on WhatsApp at 415-745-6692. You can also call us or fill out the contact form on this page. We reply fast — usually within minutes.',
      },
      {
        question: 'Are you licensed and insured?',
        answer:
          'Yes. Armando Junk Removal and Hauling is fully licensed and insured in San Francisco, CA. You can hire us with complete peace of mind.',
      },
      {
        question: 'Do you offer services in Spanish?',
        answer:
          '¡Sí! We are a bilingual team and are happy to assist you in Spanish. Se habla español.',
      },
    ],
    ctaTitle: 'Still have questions?',
    ctaBody: 'Call or text Armando directly — we reply fast.',
    ctaCall: 'Call 415-745-6692',
    ctaWhatsapp: 'Message on WhatsApp',
  },
  contact: {
    title: 'Get Your Free Quote Today',
    intro:
      "Tell us about the job and we'll get back to you with an upfront price — usually within minutes.",
    panelTitle: 'Talk to Armando',
    phoneLabel: 'PHONE',
    emailLabel: 'EMAIL',
    locationLabel: 'LOCATION',
    whatsappCta: 'Message us on WhatsApp',
    nameLabel: 'Name',
    namePlaceholder: 'Your full name',
    phoneFieldLabel: 'Phone',
    phonePlaceholder: '(415) 000-0000',
    serviceLabel: 'Service needed',
    servicePlaceholder: 'Select a service',
    serviceOptions: [
      'Junk Removal',
      'Demolition Services',
      'Construction Debris',
      'Garage Cleaning',
      'Deep Cleaning',
      'Something else',
    ],
    messageLabel: 'Describe your job',
    messagePlaceholder:
      'What needs to go? Where is it located? Any access details?',
    submit: 'Request My Free Quote',
    submitting: 'Sending…',
    success: 'Thanks! We received your request and will call you shortly.',
    error: 'Please add your name and phone number so we can reach you.',
  },
  footer: {
    tagline: 'Professional Junk Hauling Services in San Francisco, CA',
    quickLinksTitle: 'QUICK LINKS',
    links: [
      { label: 'Services', href: '#services' },
      { label: 'About', href: '#why-us' },
      { label: 'Contact', href: '#contact' },
    ],
    callTitle: 'CALL US',
    copyright:
      '© 2025 Armando Junk Removal and Hauling. All rights reserved.',
    aria: 'Footer navigation',
  },
  whatsapp: {
    aria: 'Message Armando Junk Removal on WhatsApp',
  },
}

const es: typeof en = {
  nav: {
    ariaMain: 'Navegación principal',
    ariaMobile: 'Navegación móvil',
    logoSub: 'Junk Removal',
    links: [
      { label: 'Servicios', href: '#services' },
      { label: 'Nosotros', href: '#why-us' },
      { label: 'Galería', href: '#gallery' },
      { label: 'Contacto', href: '#contact' },
    ],
    cta: 'Obtener Cotización Gratis',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    languageLabel: 'Seleccionar idioma',
  },
  hero: {
    tag: 'SAN FRANCISCO, CA 94112',
    headline:
      'Los Expertos en Retiro de Basura y Escombros de Confianza en San Francisco',
    subheadPre:
      'Rápido, accesible y confiable. Servicio el mismo día o al día siguiente. ',
    subheadStrong: 'Se habla español.',
    imageAlt:
      'Camión de volteo de Armando Junk Removal cargado de escombros cerca del Puente Golden Gate al amanecer',
    callCta: 'Llamar:',
    quoteCta: 'Cotización Gratis',
  },
  trust: {
    aria: 'Por qué puedes confiar en nosotros',
    items: [
      'Licenciado y Asegurado',
      'Servicio el Mismo Día',
      'Estimados Gratuitos',
      'Se Habla Español',
    ],
  },
  services: {
    title: 'Lo Que Hacemos',
    intro:
      'Un equipo para todo el trabajo. Residencial y comercial en todo San Francisco.',
    cardCta: 'Pedir cotización',
    items: [
      {
        title: 'Retiro de Chatarra',
        description:
          'Muebles, electrodomésticos, electrónica y más. Rápido y limpio.',
      },
      {
        title: 'Servicios de Demolición',
        description:
          'Evaluación, preparación, demolición y retiro de escombros.',
      },
      {
        title: 'Escombros de Construcción',
        description: 'Limpiamos tu obra para que sigas trabajando.',
      },
      {
        title: 'Limpieza de Garage',
        description: 'Vaciado completo de garage, organizado y eficiente.',
      },
      {
        title: 'Limpieza Profunda',
        description:
          'Servicios de limpieza profunda para espacios residenciales y comerciales. Sanitización completa y limpieza detallada.',
      },
    ],
    promptTitle: '¿No sabes qué necesitas?',
    promptBody:
      'Mándanos una foto del trabajo y te decimos exactamente qué se necesita y cuánto cuesta.',
    promptCta: 'Pregúntale a Armando',
  },
  why: {
    title: 'Por Qué San Francisco Elige a Armando',
    intro:
      'Un equipo local operado por su dueño, que contesta el teléfono, cotiza con honestidad y termina el trabajo el mismo día cuando es posible.',
    basedInLabel: 'Basado en',
    basedInValue: 'SF 94112',
    servingLabel: 'Cubriendo',
    servingValue: 'Área de la Bahía',
    items: [
      {
        title: 'Respuesta Rápida',
        description: 'Disponibilidad el mismo día o al día siguiente.',
      },
      {
        title: 'Precios Accesibles',
        description: 'Sin costos ocultos. Cotización antes de empezar.',
      },
      {
        title: 'Equipo Experimentado',
        description: 'Años de experiencia en toda el Área de la Bahía.',
      },
      {
        title: 'Servicio Completo',
        description: 'De escombros a demoliciones, lo manejamos todo.',
      },
    ],
  },
  steps: {
    title: '3 Pasos Simples',
    intro:
      'De la foto al retiro. La mayoría de los trabajos se cotizan en minutos.',
    whatsappCta: 'Mandar foto por WhatsApp',
    items: [
      {
        number: '01',
        title: 'Mándanos una foto',
        description:
          'Toma una foto de tu basura y mándala por WhatsApp. Sin visita previa necesaria.',
      },
      {
        number: '02',
        title: 'Recibe tu cotización gratis',
        description:
          'Te respondemos en minutos con un precio claro. Sin costos ocultos.',
      },
      {
        number: '03',
        title: 'Llegamos y nos lo llevamos todo',
        description:
          'Nuestro equipo llega a tiempo, limpia todo y deja el lugar impecable.',
      },
    ],
  },
  gallery: {
    title: 'Trabajos Recientes',
    intro:
      'Algunos trabajos por la ciudad. Retirado, limpiado y terminado el mismo día.',
    captions: [
      'Retiro de muebles — Excelsior',
      'Vaciado completo de garage',
      'Limpieza de escombros en obra',
      'Limpieza profunda',
    ],
  },
  testimonials: {
    title: 'Lo Que Dicen Nuestros Clientes',
    intro: 'Vecinos, propietarios y contratistas en toda la ciudad.',
    ratingAria: 'Calificación de 5 de 5 estrellas',
    items: [
      {
        name: 'Maria R.',
        location: 'Excelsior District',
        quote:
          'Mandé una foto de mi garage el sábado por la mañana y para la tarde estaba completamente vacío. Armando y su equipo fueron rápidos, amables y dejaron el piso impecable.',
      },
      {
        name: 'James T.',
        location: 'Bernal Heights',
        quote:
          'Pedí tres cotizaciones para retirar escombros. Armando fue el más económico y el único que apareció al día siguiente. Precio claro, sin sorpresas.',
      },
      {
        name: 'Carlos M.',
        location: 'Mission District',
        quote:
          'Profesionales de principio a fin. Hicieron una demolición completa, trabajaron duro todo el día y trataron mi propiedad con respeto. Los recomiendo mucho.',
      },
    ],
  },
  faq: {
    title: 'Preguntas Frecuentes',
    intro:
      'Todo lo que necesitas saber antes de reservar tu servicio de junk removal en San Francisco.',
    items: [
      {
        question: '¿Cuánto cuesta el retiro de basura en San Francisco?',
        answer:
          'Cada trabajo es diferente, por eso damos estimados gratis antes de empezar. El precio depende del volumen y del tipo de artículos. Mándanos una foto por WhatsApp y te damos un precio claro, generalmente en minutos. Sin costos ocultos.',
      },
      {
        question: '¿Ofrecen servicio el mismo día?',
        answer:
          '¡Sí! Ofrecemos servicio el mismo día o al día siguiente según disponibilidad. Llámanos al 415-745-6692 o escríbenos por WhatsApp para confirmar nuestra agenda de hoy.',
      },
      {
        question: '¿Qué tipo de basura y escombros retiran?',
        answer:
          'Retiramos casi todo: muebles, electrodomésticos, colchones, electrónica, desechos de jardín, escombros de construcción, vaciado de garages y más. Si tienes dudas, mándanos una foto y te confirmamos.',
      },
      {
        question: '¿Hay algo que no puedan llevarse?',
        answer:
          'Por razones de seguridad y por ley, no podemos transportar materiales peligrosos como químicos, pintura, asbesto o desechos médicos. Con todo lo demás te podemos ayudar.',
      },
      {
        question: '¿Tengo que estar en casa durante el retiro?',
        answer:
          'No es necesario. Si tenemos acceso a los artículos y una forma de comunicarnos contigo para el pago, no necesitas estar presente. Te enviamos una confirmación cuando el trabajo esté terminado.',
      },
      {
        question: '¿Cómo obtengo una cotización?',
        answer:
          'Lo más fácil es mandarnos una foto de los artículos por WhatsApp al 415-745-6692. También puedes llamarnos o llenar el formulario de contacto en esta página. Respondemos rápido, generalmente en minutos.',
      },
      {
        question: '¿Están licenciados y asegurados?',
        answer:
          'Sí. Armando Junk Removal and Hauling cuenta con licencia y seguro en San Francisco, CA. Puedes contratarnos con total tranquilidad.',
      },
      {
        question: '¿Ofrecen servicio en español?',
        answer:
          '¡Sí! Somos un equipo bilingüe y con gusto te atendemos en español. Se habla español.',
      },
    ],
    ctaTitle: '¿Todavía tienes preguntas?',
    ctaBody: 'Llama o escríbele a Armando directamente. Respondemos rápido.',
    ctaCall: 'Llamar 415-745-6692',
    ctaWhatsapp: 'Escribir por WhatsApp',
  },
  contact: {
    title: 'Obtené tu Cotización Gratis Hoy',
    intro:
      'Cuéntanos sobre el trabajo y te respondemos con un precio claro, generalmente en minutos.',
    panelTitle: 'Habla con Armando',
    phoneLabel: 'TELÉFONO',
    emailLabel: 'CORREO',
    locationLabel: 'UBICACIÓN',
    whatsappCta: 'Escríbinos por WhatsApp',
    nameLabel: 'Nombre',
    namePlaceholder: 'Tu nombre completo',
    phoneFieldLabel: 'Teléfono',
    phonePlaceholder: '(415) 000-0000',
    serviceLabel: 'Servicio que necesitas',
    servicePlaceholder: 'Selecciona un servicio',
    serviceOptions: [
      'Retiro de Chatarra',
      'Servicios de Demolición',
      'Escombros de Construcción',
      'Limpieza de Garage',
      'Limpieza Profunda',
      'Otro servicio',
    ],
    messageLabel: 'Describe tu trabajo',
    messagePlaceholder: '¿Qué hay que retirar? ¿Dónde está? ¿Algún detalle de acceso?',
    submit: 'Solicitar Cotización Gratis',
    submitting: 'Enviando…',
    success: '¡Gracias! Recibimos tu solicitud y te llamamos en breve.',
    error: 'Agrega tu nombre y teléfono para que podamos contactarte.',
  },
  footer: {
    tagline:
      'Servicios Profesionales de Retiro de Escombros en San Francisco, CA',
    quickLinksTitle: 'ENLACES RÁPIDOS',
    links: [
      { label: 'Servicios', href: '#services' },
      { label: 'Nosotros', href: '#why-us' },
      { label: 'Contacto', href: '#contact' },
    ],
    callTitle: 'LLÁMANOS',
    copyright:
      '© 2025 Armando Junk Removal and Hauling. Todos los derechos reservados.',
    aria: 'Navegación del pie de página',
  },
  whatsapp: {
    aria: 'Escribir a Armando Junk Removal por WhatsApp',
  },
}

export const copy: Record<Lang, typeof en> = { en, es }

export type Copy = typeof en
